import React, { useRef, useEffect, useState } from "react";
import { Play, Pause, X, Maximize2, Volume2, VolumeX, ShieldCheck, RotateCcw, PictureInPicture2 } from "lucide-react";
import Hls from "hls.js";
import { usePlayer } from "../../context/PlayerContext.js";
import { useMediaSession } from "../../hooks/useMediaSession.js";

export interface MiniPlayerProps {
  onExpand?: () => void;
  onClose?: () => void;
}

export const MiniPlayer: React.FC<MiniPlayerProps> = ({ onExpand, onClose }) => {
  const {
    activeVideo,
    isPlaying,
    currentTime,
    duration,
    isMiniplayerVisible,
    togglePlay,
    updateTime,
    closeMiniplayer,
    expandMiniplayer,
    seekTarget,
  } = usePlayer();

  const videoRef = useRef<HTMLVideoElement>(null);
  const hlsRef = useRef<Hls | null>(null);
  const [isMuted, setIsMuted] = useState(false);

  // Derive manifest / stream url
  const streamUid = activeVideo?.streamMediaId || activeVideo?.uid || activeVideo?.id;
  const manifestUrl =
    activeVideo?.hlsManifestUrl ||
    (streamUid && !streamUid.startsWith("custom_")
      ? `https://videodelivery.net/${streamUid}/manifest/video.m3u8`
      : "https://test-streams.mux.dev/x36xhzz/x36xhzz.m3u8");

  // Setup HLS / Video element
  useEffect(() => {
    if (!isMiniplayerVisible || !activeVideo) return;

    const video = videoRef.current;
    if (!video) return;

    if (Hls.isSupported() && manifestUrl.includes(".m3u8")) {
      if (hlsRef.current) {
        hlsRef.current.destroy();
      }
      const hls = new Hls({ enableWorker: false, lowLatencyMode: true });
      hlsRef.current = hls;
      hls.loadSource(manifestUrl);
      hls.attachMedia(video);

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        if (currentTime > 0) {
          video.currentTime = currentTime;
        }
        if (isPlaying) {
          video.play().catch(() => {});
        }
      });
    } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = manifestUrl;
      if (currentTime > 0) {
        video.currentTime = currentTime;
      }
      if (isPlaying) {
        video.play().catch(() => {});
      }
    } else {
      // Direct mp4 fallback
      video.src = `https://videodelivery.net/${streamUid}/downloads/default.mp4`;
      if (currentTime > 0) {
        video.currentTime = currentTime;
      }
      if (isPlaying) {
        video.play().catch(() => {});
      }
    }

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [isMiniplayerVisible, activeVideo?.id, manifestUrl]);

  // Sync play/pause state
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (isPlaying && video.paused) {
      video.play().catch(() => {});
    } else if (!isPlaying && !video.paused) {
      video.pause();
    }
  }, [isPlaying]);

  // Handle external seekTarget
  useEffect(() => {
    if (typeof seekTarget === "number" && videoRef.current) {
      videoRef.current.currentTime = seekTarget;
    }
  }, [seekTarget]);

  // Time update listener
  const handleTimeUpdate = () => {
    if (videoRef.current) {
      updateTime(videoRef.current.currentTime, videoRef.current.duration);
    }
  };

  const handleExpand = () => {
    expandMiniplayer();
    onExpand?.();
  };

  const handleClose = () => {
    if (videoRef.current) {
      videoRef.current.pause();
    }
    closeMiniplayer();
    onClose?.();
  };

  const handleRewind10 = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (videoRef.current) {
      videoRef.current.currentTime = Math.max(0, videoRef.current.currentTime - 10);
    }
  };

  const togglePictureInPicture = async (e?: React.MouseEvent) => {
    e?.stopPropagation();
    const video = videoRef.current;
    if (!video) return;
    try {
      if (document.pictureInPictureElement) {
        await document.exitPictureInPicture();
      } else if (document.pictureInPictureEnabled && video.requestPictureInPicture) {
        await video.requestPictureInPicture();
      }
    } catch (err) {
      console.warn("[MiniPlayer] PiP toggle failed:", err);
    }
  };

  // Synchronize OS-level Media Session API during background / floating playback
  useMediaSession({
    metadata: {
      title: activeVideo?.title || "HP - Helix Video",
      artist: activeVideo?.creatorName || activeVideo?.creatorHandle || "HP - Helix",
      album: "HP - Helix Streaming",
      artwork: activeVideo?.thumbnailUrl
        ? [{ src: activeVideo.thumbnailUrl, sizes: "512x512", type: "image/jpeg" }]
        : undefined,
    },
    isPlaying,
    currentTime,
    duration,
    onPlay: togglePlay,
    onPause: togglePlay,
    onSeekBackward: (offset = 10) => {
      if (videoRef.current) {
        videoRef.current.currentTime = Math.max(0, videoRef.current.currentTime - offset);
      }
    },
    onSeekForward: (offset = 10) => {
      if (videoRef.current) {
        videoRef.current.currentTime = Math.min(
          videoRef.current.duration || duration,
          videoRef.current.currentTime + offset
        );
      }
    },
    onSeekTo: (time) => {
      if (videoRef.current) {
        videoRef.current.currentTime = time;
      }
    },
    onStop: handleClose,
  });

  useEffect(() => {
    if (!isMiniplayerVisible) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (["INPUT", "TEXTAREA", "SELECT"].includes((e.target as HTMLElement).tagName)) {
        return;
      }
      if (e.key.toLowerCase() === "i") {
        e.preventDefault();
        handleExpand();
      } else if (e.key.toLowerCase() === "k" || e.key === " ") {
        e.preventDefault();
        togglePlay();
      } else if (e.key.toLowerCase() === "m") {
        e.preventDefault();
        setIsMuted((prev) => !prev);
      } else if (e.key.toLowerCase() === "p") {
        e.preventDefault();
        if (videoRef.current) {
          if (document.pictureInPictureElement) {
            document.exitPictureInPicture().catch(() => {});
          } else if (document.pictureInPictureEnabled && videoRef.current.requestPictureInPicture) {
            videoRef.current.requestPictureInPicture().catch(() => {});
          }
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isMiniplayerVisible, togglePlay]);

  if (!isMiniplayerVisible || !activeVideo) {
    return null;
  }

  const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div
      role="region"
      aria-label="Floating Miniplayer"
      className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50 w-[320px] sm:w-[380px] bg-zinc-950/95 backdrop-blur-2xl border border-zinc-800/90 rounded-2xl shadow-2xl shadow-black/80 overflow-hidden font-sans select-none animate-in slide-in-from-bottom-6 fade-in duration-200 group"
    >
      {/* Mini Video Container & Content Row */}
      <div className="flex items-center p-2.5 gap-3">
        {/* Compact 16:9 Viewport */}
        <div
          onClick={handleExpand}
          className="relative w-[124px] sm:w-[136px] aspect-video bg-black rounded-xl overflow-hidden shrink-0 cursor-pointer border border-zinc-800/80 shadow-md group-hover:border-emerald-500/50 transition-colors"
          title="Click to expand video"
        >
          <video
            ref={videoRef}
            className="w-full h-full object-cover"
            playsInline
            muted={isMuted}
            onTimeUpdate={handleTimeUpdate}
            poster={activeVideo.custom_thumbnail || activeVideo.thumbnailUrl}
          />
          {/* Subtle Hover Expand Overlay */}
          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
            <Maximize2 className="w-5 h-5 text-white drop-shadow-md" />
          </div>
        </div>

        {/* Video Info (Clickable to Expand) */}
        <div
          onClick={handleExpand}
          className="flex-1 min-w-0 cursor-pointer space-y-1"
          title={`Expand: ${activeVideo.title}`}
        >
          <p className="text-xs font-bold text-white truncate leading-snug group-hover:text-emerald-300 transition-colors">
            {activeVideo.title}
          </p>
          <div className="flex items-center gap-1.5 text-[11px] text-zinc-400 truncate">
            <span className="truncate">{activeVideo.creatorName}</span>
            <ShieldCheck className="w-3 h-3 text-emerald-400 shrink-0" />
          </div>
        </div>

        {/* Transport Actions */}
        <div className="flex items-center gap-1 shrink-0 pl-1">
          {/* Play/Pause */}
          <button
            type="button"
            onClick={togglePlay}
            className="w-8 h-8 rounded-xl bg-zinc-900 hover:bg-emerald-500 hover:text-zinc-950 text-white flex items-center justify-center transition-all border border-zinc-800 hover:border-emerald-400 shadow-sm active:scale-95 cursor-pointer"
            title={isPlaying ? "Pause" : "Play"}
          >
            {isPlaying ? (
              <Pause className="w-4 h-4 fill-current" />
            ) : (
              <Play className="w-4 h-4 ml-0.5 fill-current" />
            )}
          </button>

          {/* Rewind 10s */}
          <button
            type="button"
            onClick={handleRewind10}
            className="w-8 h-8 rounded-xl bg-zinc-900 hover:bg-zinc-800 text-zinc-300 hover:text-white flex items-center justify-center transition-all border border-zinc-800 active:scale-95 cursor-pointer"
            title="Rewind 10s"
          >
            <RotateCcw className="w-3.5 h-3.5" />
          </button>

          {/* Mute / Unmute */}
          <button
            type="button"
            onClick={() => setIsMuted(!isMuted)}
            className="w-8 h-8 rounded-xl bg-zinc-900 hover:bg-zinc-800 text-zinc-300 hover:text-white flex items-center justify-center transition-all border border-zinc-800 active:scale-95 cursor-pointer"
            title={isMuted ? "Unmute" : "Mute"}
          >
            {isMuted ? <VolumeX className="w-3.5 h-3.5 text-red-400" /> : <Volume2 className="w-3.5 h-3.5" />}
          </button>

          {/* Picture-in-Picture */}
          <button
            type="button"
            onClick={togglePictureInPicture}
            className="w-8 h-8 rounded-xl bg-zinc-900 hover:bg-zinc-800 text-zinc-300 hover:text-white flex items-center justify-center transition-all border border-zinc-800 active:scale-95 cursor-pointer"
            title="Picture-in-Picture (p)"
          >
            <PictureInPicture2 className="w-3.5 h-3.5" />
          </button>

          {/* Expand */}
          <button
            type="button"
            onClick={handleExpand}
            className="w-8 h-8 rounded-xl bg-zinc-900 hover:bg-zinc-800 text-zinc-300 hover:text-white flex items-center justify-center transition-all border border-zinc-800 active:scale-95 cursor-pointer"
            title="Expand to Full Watch Mode (i)"
          >
            <Maximize2 className="w-3.5 h-3.5" />
          </button>

          {/* Close */}
          <button
            type="button"
            onClick={handleClose}
            className="w-8 h-8 rounded-xl bg-zinc-900 hover:bg-zinc-800 text-zinc-400 hover:text-rose-400 flex items-center justify-center transition-all border border-zinc-800 active:scale-95 cursor-pointer"
            title="Close Miniplayer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Synchronized Progress Track */}
      <div className="w-full bg-zinc-800/80 h-1 relative overflow-hidden">
        <div
          className="h-full bg-emerald-500 transition-all duration-150 shadow-sm shadow-emerald-500/50"
          style={{ width: `${Math.min(100, Math.max(0, progressPercent))}%` }}
        />
      </div>
    </div>
  );
};
